// ---------------------------------------------------------------------------
// ShareSignalDialog — Consent + success + payload preview dialogs for sharing
// a signal with other AI Studio users.
// ---------------------------------------------------------------------------

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Link,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import {
  Checkmark16Regular,
  CheckmarkCircle16Filled,
  Copy16Regular,
  Document24Regular,
  Share24Regular,
} from '@fluentui/react-icons';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useHttpClient } from '../../../api/HttpClientContext.js';
import { shareSignal } from '../../../services/sharedSignalsService.js';
import type {
  EvalAssessment,
  ResponseQuality,
} from '../../../types/sharedSignal.js';
import { getSavedToken } from '../../../utils/sigsTokenStorage.js';

export interface ShareSignalDialogProps {
  signalId: string;
  itemId: string;
  agentType: string;
  userPrompt: string;
  rawSignal: unknown;
  disabled?: boolean;
}

export function ShareSignalDialog({
  signalId,
  itemId,
  agentType,
  userPrompt,
  rawSignal,
  disabled,
}: ShareSignalDialogProps) {
  const httpClient = useHttpClient();
  const [searchParams] = useSearchParams();

  const [consentOpen, setConsentOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareDescription, setShareDescription] = useState('');
  const [responseQuality, setResponseQuality] =
    useState<ResponseQuality>('neutral');
  const [evalAssessment, setEvalAssessment] =
    useState<EvalAssessment>('neutral');
  const [payloadPreviewOpen, setPayloadPreviewOpen] = useState(false);

  const handleShareAgree = useCallback(() => {
    setConsentOpen(false);
    setShareLoading(true);

    void (async () => {
      try {
        const substrateToken = getSavedToken() || undefined;
        await shareSignal(
          httpClient,
          signalId,
          substrateToken,
          itemId,
          shareDescription || undefined,
          agentType,
          userPrompt?.slice(0, 200) || undefined,
          responseQuality,
          evalAssessment,
        );
        const cvFromUrl = searchParams.get('cv') ?? itemId;
        const url = `${window.location.origin}/debug?cv=${encodeURIComponent(cvFromUrl)}`;
        setShareUrl(url);
        void navigator.clipboard.writeText(url);
        setSuccessOpen(true);
        setShareDescription('');
        setResponseQuality('neutral');
        setEvalAssessment('neutral');
      } catch (err) {
        console.error('Failed to share signal:', err);
      } finally {
        setShareLoading(false);
      }
    })();
  }, [
    httpClient,
    signalId,
    itemId,
    agentType,
    userPrompt,
    searchParams,
    shareDescription,
    responseQuality,
    evalAssessment,
  ]);

  return (
    <>
      {/* Share button + consent dialog */}
      <Dialog
        open={consentOpen}
        onOpenChange={(_, d) => {
          setConsentOpen(d.open);
        }}
      >
        <DialogTrigger disableButtonEnhancement>
          <Button
            appearance="outline"
            icon={shareLoading ? <Spinner size="tiny" /> : <Share24Regular />}
            size="small"
            disabled={disabled === true || shareLoading}
          >
            Share
          </Button>
        </DialogTrigger>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Share Your Data</DialogTitle>
            <DialogContent>
              <p>
                We would like to ask for your consent to share your AI
                interaction data, including your utterance, bot response, search
                queries, LLM prompts, data used to generate the bot response,
                and personal information (name, organization, etc.).
              </p>
              <div style={{ marginTop: tokens.spacingVerticalM }}>
                <Text size={200} weight="semibold" block>
                  Was the agent response good, neutral, or bad?
                </Text>
                <RadioGroup
                  aria-label="Agent response quality"
                  layout="horizontal"
                  value={responseQuality ?? ''}
                  onChange={(_, data) =>
                    setResponseQuality(data.value as ResponseQuality)
                  }
                >
                  <Radio value="good" label="Good" />
                  <Radio value="neutral" label="Neutral" />
                  <Radio value="bad" label="Bad" />
                </RadioGroup>
              </div>
              <div style={{ marginTop: tokens.spacingVerticalM }}>
                <Text size={200} weight="semibold" block>
                  Do you agree with the evals?
                </Text>
                <RadioGroup
                  aria-label="Eval assessment"
                  layout="horizontal"
                  value={evalAssessment ?? ''}
                  onChange={(_, data) =>
                    setEvalAssessment(data.value as EvalAssessment)
                  }
                >
                  <Radio value="agree" label="Agree" />
                  <Radio value="neutral" label="Neutral" />
                  <Radio value="disagree" label="Disagree" />
                </RadioGroup>
              </div>
              <div style={{ marginTop: tokens.spacingVerticalM }}>
                <Text size={200} weight="semibold" block>
                  Description (optional, max 1000 characters)
                </Text>
                <Textarea
                  placeholder="Describe what you're sharing and why..."
                  value={shareDescription}
                  onChange={(_, d) =>
                    setShareDescription((d.value ?? '').slice(0, 1000))
                  }
                  maxLength={1000}
                  resize="none"
                  style={{
                    width: '100%',
                    marginTop: tokens.spacingVerticalXS,
                    minHeight: '100px',
                  }}
                />
                <Text size={100} style={{ opacity: 0.6 }}>
                  {shareDescription.length}/1000
                </Text>
              </div>
              <div
                style={{
                  marginTop: tokens.spacingVerticalM,
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacingHorizontalS,
                  padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
                  border: `1px solid ${tokens.colorNeutralStroke1}`,
                  borderRadius: tokens.borderRadiusMedium,
                  cursor: 'pointer',
                }}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setConsentOpen(false);
                  setPayloadPreviewOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setConsentOpen(false);
                    setPayloadPreviewOpen(true);
                  }
                }}
              >
                <Document24Regular />
                <Link as="span" style={{ fontSize: tokens.fontSizeBase200 }}>
                  signalPayload.json
                </Link>
              </div>
              <Text
                size={200}
                style={{
                  marginTop: tokens.spacingVerticalM,
                  display: 'block',
                }}
              >
                <Link
                  href="https://www.microsoft.com/en-us/privacy/privacystatement"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 'inherit' }}
                >
                  Learn more
                </Link>{' '}
                about how this data is used and your rights. By clicking Agree,
                your data will be used to improve Microsoft products and
                services.{' '}
                <Link
                  href="https://www.microsoft.com/en-us/privacy/privacystatement"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 'inherit' }}
                >
                  Privacy statement.
                </Link>
              </Text>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                disabled={!responseQuality || !evalAssessment}
                onClick={handleShareAgree}
              >
                Agree
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Success dialog */}
      <Dialog
        open={successOpen}
        onOpenChange={(_, d) => setSuccessOpen(d.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <CheckmarkCircle16Filled
                  style={{ color: tokens.colorPaletteGreenForeground1 }}
                />
                URL copied to clipboard
              </span>
            </DialogTitle>
            <DialogContent>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacingHorizontalS,
                }}
              >
                <Input
                  readOnly
                  value={shareUrl}
                  style={{ flex: 1 }}
                  size="small"
                />
                <Button
                  appearance="outline"
                  icon={urlCopied ? <Checkmark16Regular /> : <Copy16Regular />}
                  size="small"
                  onClick={() => {
                    void navigator.clipboard.writeText(shareUrl);
                    setUrlCopied(true);
                    setTimeout(() => setUrlCopied(false), 1500);
                  }}
                >
                  Copy
                </Button>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => setSuccessOpen(false)}
              >
                Done
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Payload preview dialog */}
      <Dialog
        open={payloadPreviewOpen}
        onOpenChange={(_, d) => {
          setPayloadPreviewOpen(d.open);
          if (!d.open) setConsentOpen(true);
        }}
      >
        <DialogSurface style={{ maxWidth: '700px', maxHeight: '80vh' }}>
          <DialogBody>
            <DialogTitle>signalPayload.json</DialogTitle>
            <DialogContent>
              <pre
                style={{
                  fontFamily: "'Cascadia Code', 'Consolas', monospace",
                  fontSize: tokens.fontSizeBase200,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  backgroundColor: tokens.colorNeutralBackground3,
                  padding: tokens.spacingVerticalM,
                  borderRadius: tokens.borderRadiusMedium,
                }}
              >
                {JSON.stringify(rawSignal, null, 2)}
              </pre>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setPayloadPreviewOpen(false);
                  setConsentOpen(true);
                }}
              >
                Back
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
